import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type { VV } from '@semios/app-platform-value-type-definitions'
import {
  getValueTypeFromEmitterType,
  IRRIGATION_PRESSURE_VALUE_TYPE_PREFIX,
} from '@semios/app-platform-values-helpers-and-types'
import { getIrrigationStatusColors } from 'App/Map/_utils/getIrrigationStatusColors'
import { getIdAndEmitterTypeFromZoneEmitterTypeKey } from 'App/Map/_utils/irrigationZoneEmitterTypeKeyUtil'
import { IconInfo } from 'components/icons/IconInfo'
import { NameWithExternalVendorIcons } from 'components/NameWithExternalVendorIcons/NameWithExternalVendorIcons'
import type { StackedChartSection, StackedChartSectionItem } from 'components/StackedChart/types'
import type { TooltipFormatterContextObject } from 'highcharts'
import { translate } from 'i18n/i18n'
import { isNil } from 'lodash'
import moment from 'moment-timezone'
import { colors } from 'settings/colors'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TSelectedFieldAssetsStoreState } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { getPointExternalVendorNames } from 'utils/getPointExternalVendorNames'
import { minutesToHoursAndMinutes } from 'utils/minutesToHoursAndMinutes'
import { sortByKey } from 'utils/sortByKey'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { getAppliedWater } from '../../_utils/getAppliedWater'
import { getIntervalForCurrentStamp } from '../../_utils/getIntervalForCurrentStamp'
import { getTooltipForActivityIntervals } from '../../_utils/getTooltipForActivityIntervals'
import { IrrigationChartLegend } from '../../_utils/IrrigationChartLegend'
import type { TSplineSeriesData } from '../../types'
import type { getActivityIntervalsFromData } from './getActivityIntervalsFromData'
import { processPressureData } from './processPressureData'

const pressureSeries = 'pressure'

export const getChartDataWithPressureAndActivity = ({
  activityIntervals,
  property,
  timezone,
  selectedFieldAssets,
  data,
  showPressure,
  commonReturnItems,
}: {
  activityIntervals: ReturnType<typeof getActivityIntervalsFromData>
  property: number | null
  timezone: string
  data: routes.Values.Response
  selectedFieldAssets: TSelectedFieldAssetsStoreState
  showPressure: boolean
  commonReturnItems: Pick<StackedChartSection, 'id' | 'title' | 'fieldAssetPicker'>
}) => {
  const { dateFrom, dateTo } = detailsPanelStore.getState()
  const blankSeries: TSplineSeriesData[] = [] // an invisible series so that we get a tooltip even when no data
  const endStamp = moment.tz(dateTo, timezone).valueOf()

  let stamp = moment.tz(dateFrom, timezone).valueOf()

  while (stamp <= endStamp) {
    blankSeries.push({ x: stamp, y: 10 })

    stamp = stamp + 60 * 60 * 1000
  }

  const propertyData = fieldAssetStore.getState()?.properties?.[property as number]
  const points = propertyData?.points ?? {}
  const { pressure: pressureUnitFromUserSetting } = userDetailsStore.getState()
  const pressureUnit = unitConverter.irrigationPressure().suffix()
  const { irrigationZoneEmitter, pressureTransducerPoint } = selectedFieldAssets

  const emitterType =
    irrigationZoneEmitter && getIdAndEmitterTypeFromZoneEmitterTypeKey(irrigationZoneEmitter).emitterType

  const pointsForZone =
    irrigationZoneEmitter && pressureTransducerPoint
      ? Object.values(points).filter((p) => {
          return (
            emitterType &&
            p.configuration.irrigationEmitterTypesAvailable?.includes(emitterType) &&
            p.irrigationEmitterZoneIds?.includes(irrigationZoneEmitter) &&
            p.valuesTimeseries.some((vt) => vt.includes('Pressure')) // redundant?
          )
        })
      : []

  const valueType =
    emitterType &&
    (getValueTypeFromEmitterType(
      IRRIGATION_PRESSURE_VALUE_TYPE_PREFIX,
      emitterType,
    ) as keyof VV.DomainTypes.Irrigation.TValuesReturnWithMetaIgnoringKeying['points'])

  const pressureDataLngLatMap = pointsForZone.reduce<
    Record<
      string,
      NonNullable<NonNullable<VV.Response['points']>[string]['values']['irrigationPressureSemios_pump']>
    >
  >((acc, point) => {
    const pressureDataForPoint = valueType && data?.points?.[point.lngLat]?.values?.[valueType]

    if (pressureDataForPoint) {
      acc[point.lngLat] = (acc[point.lngLat] || []).concat(pressureDataForPoint as (typeof acc)[string])
    }

    return acc
  }, {})

  const flowRate =
    (selectedFieldAssets?.irrigationZoneEmitter &&
      (propertyData?.irrigationZoneEmitters ?? {})[selectedFieldAssets?.irrigationZoneEmitter]?.flowRate
        .mmPerHour) ??
    0

  const isPressureMetric = pressureUnitFromUserSetting === 'METRIC'
  const showPressureInMetric = showPressure && isPressureMetric

  const shapedDataForCharts = pointsForZone
    .map((point) => {
      const activityIntervalsForPoint = activityIntervals?.[point.lngLat]?.[0]

      let onDurationMinutes = 0

      const intervalsForChart = (activityIntervalsForPoint?.timeseries ?? []).map((s, index) => {
        if (s.status === 'on') {
          const periodMinutes = moment.tz(s.endTime, timezone).diff(s.startTime, 'minutes')

          onDurationMinutes += periodMinutes
        }

        return {
          key: `${index}`,
          name: 'Applied' as const,
          x: s.startTime ? moment.tz(s.startTime, timezone).valueOf() : moment.tz(timezone).valueOf(),
          x2: s.endTime ? moment.tz(s.endTime, timezone).valueOf() : moment.tz(timezone).valueOf(),
          y: showPressureInMetric ? 300 : 50,
          color: getIrrigationStatusColors(s.status).backgroundColor,
          status: s.status,
          pointWidth: s.status === 'on' ? 24 : 14,
        }
      })

      const totalWaterApplied = onDurationMinutes ? +((onDurationMinutes / 60) * flowRate) : 0

      return {
        point,
        deviceName: point.name,
        onDurationMinutes,
        intervalsForDevice: activityIntervalsForPoint,
        intervalsForChart,
        totalWaterApplied,
        pressureData: processPressureData(pressureDataLngLatMap, point.lngLat, showPressure, timezone),
      }
    })
    .sort(sortByKey('deviceName'))

  return {
    ...commonReturnItems,
    items: shapedDataForCharts.map((chartRow, i) => {
      const pressureSeriesId = `${pressureSeries}-${i}`

      const {
        totalWaterApplied,
        intervalsForChart: originalIntervals,
        deviceName,
        pressureData,
        onDurationMinutes,
        intervalsForDevice,
      } = chartRow

      const hasPressureData =
        pressureData.length > 0 && pressureData.some((point) => point.y !== null && point.y !== undefined)

      const intervalsForChart = hasPressureData
        ? originalIntervals
        : originalIntervals.map((i) => ({ ...i, y: 10 }))

      const allChartSeries = []

      if (pressureData.length > 0 || intervalsForChart.length > 0) {
        allChartSeries.push({
          color: 'rgba(0, 0, 0, 0)',
          type: 'spline' as const,
          name: 'invisible',
          showInLegend: false,
          data: blankSeries,
          enableMouseTracking: true,
          id: 'blank',
          marker: {
            enabled: false,
          },
        })
      }

      allChartSeries.push({
        type: 'xrange' as const,
        name: translate.phrases.banyanApp('Irrigation Activity'),
        showInLegend: false,
        minPointLength: 3,
        data: intervalsForChart,
        enableMouseTracking: true,
        id: 'irrigationIntervals',
      })

      if (hasPressureData) {
        allChartSeries.push({
          color: colors.midnight,
          type: 'spline' as const,
          name: translate.phrases.banyanApp('Pressure'),
          showInLegend: false,
          data: pressureData,
          enableMouseTracking: true,
          id: pressureSeriesId,
          marker: {
            enabled: false,
          },
        })
      }

      const maxTickForYAxis = showPressureInMetric ? 360 : 60
      const tickIntervalForYAxis = showPressureInMetric ? 90 : 20

      const mainChartItem: StackedChartSectionItem = {
        chartConfig: {
          semiosHighchartsAdditions: {
            id: `Pressure Sensors - ${deviceName} - ${pressureSeriesId}`,
            firstForecastTimestamp: +new Date(),
          },
          chart: {
            type: 'spline' as const,
            height: hasPressureData ? 250 : 150,
          },
          tooltip: {
            outside: true,
            xDateFormat: '%Z' as const,
            formatter: function (this: TooltipFormatterContextObject) {
              const timestamp = this.x

              if (!timestamp) return false

              const { start, end, status } = getIntervalForCurrentStamp({
                currentTimeStamp: timestamp,
                intervals: intervalsForDevice.timeseries,
                timezone,
              })

              if (!start) return false

              let appliedWater = (moment.tz(end, timezone).diff(start, 'minutes') / 60) * flowRate

              const pressureRow = this?.points?.find(
                (pData) => pData?.series.userOptions?.id === pressureSeriesId,
              )

              const toolTipParams = {
                currentTimeStamp: translate.dates.format(
                  moment.tz(timestamp, timezone),
                  'ddd, MMM D, h:mm a (z)',
                ),
                appliedWater: getAppliedWater({ status, appliedWater }),
                showPressure: showPressure && hasPressureData,
                pressureValue:
                  hasPressureData && !isNil(pressureRow?.y)
                    ? `${pressureRow?.y} ${pressureUnit}`
                    : translate.phrases.templates('-'),
                status,
                periodDurationText: end
                  ? minutesToHoursAndMinutes(moment.tz(end, timezone).diff(start, 'minutes'))
                  : translate.phrases.templates('-'),
                periodFromDateText: translate.dates.format(start, 'ddd, MMM D, h:mm a (z)'),
                periodToDateText: end
                  ? translate.dates.format(end, 'ddd, MMM D, h:mm a (z)')
                  : translate.phrases.templates('-'),
              }

              return getTooltipForActivityIntervals(toolTipParams)
            },
          },
          yAxis: {
            min: 0,
            softMax: hasPressureData ? maxTickForYAxis : 1,
            tickInterval: tickIntervalForYAxis,
            visible: showPressure && hasPressureData,
            labels: {
              style: {
                fontSize: '14px',
              },
            },
          },
          plotOptions: {
            series: {
              states: {
                // don't fade out these series when hovering over the chart
                inactive: {
                  opacity: 1,
                },
              },
            },
          },
          series: allChartSeries,
        },

        childrenLower: (
          <div
            css={{
              display: 'flex',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            <div css={{ flex: 1 }}>
              <IrrigationChartLegend showPressure={showPressure && hasPressureData} />
              {!showPressure && (
                <div
                  css={{
                    alignItems: 'center',
                    display: 'flex',
                    fontSize: 14,
                    gap: 5,
                    padding: '10px 20px',
                  }}
                >
                  <span css={{ color: colors.primary, marginRight: 5, marginTop: '0.2em' }}>
                    <IconInfo />
                  </span>
                  <span>
                    {translate.phrases.banyanApp('Pressure values are only available for periods < 31 days')}
                  </span>
                </div>
              )}
            </div>
            <div
              css={{
                textAlign: 'center',
                fontSize: '14px',
                color: colors.grey900,
                margin: '10px 10px 15px',
                display: 'flex',
              }}
            >
              <div>
                {translate.phrases.templates('{{label}}: {{value}}', {
                  label: translate.phrases.banyanApp('On Duration'),
                  value: minutesToHoursAndMinutes(onDurationMinutes),
                })}
              </div>
              <div css={{ padding: '0 10px' }}>{'|'}</div>
              <div>
                {translate.phrases.templates('{{label}}: {{value}}', {
                  label: translate.phrases.banyanApp('Total Volume'),
                  value: unitConverter
                    .irrigationActivity(totalWaterApplied, { decimalPlaces: 2 })
                    .valueWithSuffix(),
                })}
              </div>
            </div>
          </div>
        ),
        childrenUpper: (
          <div css={{ fontWeight: 'bold', padding: '20px 30px 0 30px' }}>
            <NameWithExternalVendorIcons
              name={deviceName}
              externalVendorNames={getPointExternalVendorNames({
                point: chartRow.point,
                deviceKind: 'pressureSensor',
              })}
            />
          </div>
        ),
      }

      return mainChartItem
    }),
  }
}
